import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { FC, useState } from "react";
import { CaseItem, isShortageEscalationCaseItem } from "src/types/case-items";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { EMPTY_ARRAY } from "src/utils/empty-values";
import { FetchCaseItemsArgs, GetCaseItemsResponse } from "src/pages/UserDashboard/Cases/api/types";
import { disputeAmountPopupItemColumns } from "../columns";
import { CaseItemKeys } from "src/pages/UserDashboard/Cases/mappers/mapServerToLocalCase";
import { Form, Formik } from "formik";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCaseItemsData } from "src/pages/UserDashboard/Cases/queries";
import { CaseType } from "../../../../case-types";
import { formatMaybeCurrency } from "src/pages/UserDashboard/Cases/mappers/utils";
import { withBackdropCloseGuard } from "src/utils/dialog/with-backdrop-close-guard";
import { toast } from "react-hot-toast";
import {
  BatchUpdateCaseItemParams,
  batchUpdateCaseItems,
  BatchUpdateCaseItemsParams,
} from "src/pages/UserDashboard/Cases/api/casesApi";
import { DisputeAmountConfirmationPopup } from "./DisputeAmountConfirmationPopup";

interface DisputeAmountPopupProps {
  caseId: string;
  onClose: () => void;
  open: boolean;
  fetchCaseItemsParams?: FetchCaseItemsArgs;
}

export const DisputeAmountPopup: FC<DisputeAmountPopupProps> = ({ caseId, onClose, open, fetchCaseItemsParams }) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const queryClient = useQueryClient();
  let pageSize = 1000;
  let shouldUseCache = false;

  if (fetchCaseItemsParams) {
    const cachedData: GetCaseItemsResponse | undefined = queryClient.getQueryData([
      "cases",
      CaseType.SHORTAGE_ESCALATION,
      caseId,
      "items",
      fetchCaseItemsParams,
    ]);
    pageSize = cachedData?.total_records ?? pageSize;
    shouldUseCache = !!cachedData?.items && cachedData?.total_records === cachedData?.items?.length;
  }

  const fetchCaseItemsArgs: FetchCaseItemsArgs = {
    page: 1,
    pageSize: pageSize,
  };

  const args = (shouldUseCache && fetchCaseItemsParams) || fetchCaseItemsArgs;

  const dataQuery = useQuery({
    queryFn: getCaseItemsData,
    queryKey: ["cases", CaseType.SHORTAGE_ESCALATION, caseId, "items", args],
    staleTime: 30000,
    placeholderData: keepPreviousData,
    enabled: !shouldUseCache,
  });

  const mutation = useMutation({
    mutationFn: async (params: BatchUpdateCaseItemsParams) => batchUpdateCaseItems(params),
    mutationKey: ["update-case-items"],
    onSuccess: async (_data, { caseId }, _onMutationResult, context) => {
      toast.success("Dispute amounts updated successfully!");
      context.client.invalidateQueries({ queryKey: ["cases", CaseType.SHORTAGE_ESCALATION, caseId, "items"] });
      context.client.invalidateQueries({
        queryKey: ["case-logs", caseId, CaseType.SHORTAGE_ESCALATION],
        refetchType: "none",
      });
      onClose();
      setShowConfirmation(false);
    },
    onError: () => {
      toast.error("Failed to update dispute amounts");
    },
  });

  const handleSubmit = async (values: CaseItem[]) => {
    const initialValuesMap = dataQuery.data?.items.reduce<Record<string, CaseItem>>((acc, item) => {
      acc[item[CaseItemKeys.CaseItemId]] = item;
      return acc;
    }, {});

    const itemsWithChanges = values.reduce<BatchUpdateCaseItemParams[]>((acc, item) => {
      const initialCaseItem = initialValuesMap?.[item[CaseItemKeys.CaseItemId]];
      if (!initialCaseItem) return acc;
      if (isShortageEscalationCaseItem(initialCaseItem) && isShortageEscalationCaseItem(item)) {
        const initialRemainingOpenBalance = initialCaseItem[CaseItemKeys.RemainingOpenBalance];
        const newRemainingOpenBalance = item[CaseItemKeys.RemainingOpenBalance];

        if (initialRemainingOpenBalance !== newRemainingOpenBalance) {
          acc.push({
            caseItemId: item[CaseItemKeys.CaseItemId],
            remainingBalanceOpen: newRemainingOpenBalance,
          });
        }
      }
      return acc;
    }, []);

    const updateValues: BatchUpdateCaseItemsParams = {
      caseId,
      items: itemsWithChanges,
    };

    await mutation.mutateAsync({
      caseId,
      items: updateValues.items,
    });
  };

  return (
    <Formik enableReinitialize initialValues={dataQuery.data?.items || EMPTY_ARRAY} onSubmit={handleSubmit}>
      {({ values, submitForm, dirty }) => {
        const totalDisputedAmounts = formatMaybeCurrency(
          values.reduce((acc, item) => {
            if (isShortageEscalationCaseItem(item)) {
              return acc + (item[CaseItemKeys.RemainingOpenBalance] ?? 0);
            }
            return acc;
          }, 0),
        );
        return (
          <Dialog
            open={open}
            onClose={withBackdropCloseGuard({ onClose, enabled: dirty })}
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown={dirty}
          >
            <DialogTitle>Edit Dispute Amounts</DialogTitle>
            <Form>
              <DialogContent>
                <DataGridPro<CaseItem>
                  rows={values}
                  rowCount={dataQuery.data?.total_records ?? 0}
                  getRowId={row => row.id}
                  loading={dataQuery.isFetching}
                  hideFooter
                  columns={disputeAmountPopupItemColumns}
                  rowHeight={70}
                  disableRowSelectionOnClick
                />
              </DialogContent>
              <DialogActions sx={{ backgroundColor: "transparent", borderTop: "1px solid #E4E4E7" }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" color="textPrimary" fontWeight={500}>
                    Total:&nbsp;{totalDisputedAmounts}
                  </Typography>
                </Box>
                <Button className="contained-reversed" variant="contained" onClick={() => onClose()}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  onClick={() => setShowConfirmation(true)}
                  disabled={!dirty}
                  sx={{ paddingX: "24px" }}
                >
                  Save Changes
                </Button>
              </DialogActions>
              {showConfirmation && (
                <DisputeAmountConfirmationPopup
                  open={showConfirmation}
                  onClose={() => setShowConfirmation(false)}
                  onConfirm={submitForm}
                  totalDisputedAmounts={totalDisputedAmounts}
                  isSubmitting={mutation.isPending}
                />
              )}
            </Form>
          </Dialog>
        );
      }}
    </Formik>
  );
};
